import React, { useState } from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails, Button, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';

const faqCategories = [
  {
    category: 'Repairs & Service',
    faqs: [
      {
        q: 'How long does a typical repair take?',
        a: 'Most repairs are completed within 24–48 hours. Same-day service is available for common repairs like screen replacements and battery swaps. Complex issues such as motherboard repairs or data recovery may take 3–5 business days.',
      },
      {
        q: 'Do you offer a warranty on repairs?',
        a: 'Yes — all repairs come with a 90-day warranty on both parts and labor. If the same issue recurs within 90 days of your repair, we fix it at no charge.',
      },
      {
        q: 'What if you can\'t fix my device?',
        a: 'We operate on a "no fix, no fee" policy for diagnostics. If we cannot repair your device, you won\'t be charged for the diagnostic fee.',
      },
      {
        q: 'Do you use genuine replacement parts?',
        a: 'We use high-quality OEM or equivalent parts for all repairs. For Apple devices, we use premium-grade components that meet Apple\'s specifications.',
      },
      {
        q: 'Do I need to bring my device in or can you come to me?',
        a: 'Both options are available. You can drop off at 102 River Street, Lynn, MA 01905, or we offer on-site service throughout Lynn and surrounding areas. Remote support is also available for software issues.',
      },
    ],
  },
  {
    category: 'Pricing & Payment',
    faqs: [
      {
        q: 'How much does a diagnostic cost?',
        a: 'Diagnostics are $29 for hardware issues. However, if you proceed with the repair, the diagnostic fee is waived — so it\'s effectively free when we fix your device.',
      },
      {
        q: 'Do you offer free estimates?',
        a: 'Yes. After running diagnostics, we provide a full written estimate before any repair begins. You are never charged without your approval.',
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept cash, all major credit/debit cards, Venmo, Zelle, and PayPal.',
      },
      {
        q: 'Do you offer monthly IT support plans?',
        a: 'Yes — we offer Starter ($199/mo) and Business ($399/mo) support plans that include priority remote support, proactive monitoring, and regular check-ins. Contact us for details.',
      },
    ],
  },
  {
    category: 'IT & Business Services',
    faqs: [
      {
        q: 'What areas do you serve?',
        a: 'We are based in Lynn, MA and serve the greater Boston North Shore area including Lynn, Saugus, Salem, Peabody, Marblehead, Swampscott, Revere, and surrounding towns. Remote IT support is available nationwide.',
      },
      {
        q: 'Are you certified?',
        a: 'Yes — Abderrazak holds a CompTIA CySA+ certification and has 16+ years of enterprise IT experience working with companies like Société Générale, Nestlé, and Velonex Technologies.',
      },
      {
        q: 'Can you manage our Microsoft 365 environment?',
        a: 'Absolutely. We provide full Microsoft 365 and Azure administration including Exchange Online, SharePoint, Teams, Intune MDM, Entra ID (Azure AD), and hybrid cloud setups.',
      },
      {
        q: 'Can you help secure our business network?',
        a: 'Yes. We offer comprehensive cybersecurity services including firewall configuration (ASA, Pfsense, SonicWall), Zero Trust implementation, Cisco ISE NAC, VPN setup, network security audits, and ongoing monitoring.',
      },
      {
        q: 'Do you offer remote IT support?',
        a: 'Yes — remote support is available for software issues, cloud management, cybersecurity, and general IT troubleshooting. Most issues can be resolved remotely within the same business day.',
      },
    ],
  },
  {
    category: 'Data & Privacy',
    faqs: [
      {
        q: 'Is my data safe during a repair?',
        a: 'Your data privacy is our top priority. We never access personal files unless necessary for diagnostics. All data remains confidential and is never shared with third parties.',
      },
      {
        q: 'Can you recover data from a dead device?',
        a: 'In many cases, yes. We offer professional data recovery for hard drives, SSDs, flash drives, and RAID arrays. Recovery success depends on the type and extent of the damage. We provide a free assessment before quoting.',
      },
      {
        q: 'Do you back up data before repairing?',
        a: 'We strongly recommend backing up your data before any repair. If your device can power on, we can help you create a backup before starting work at a small additional cost.',
      },
    ],
  },
];

function FAQ() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: '6%', right: '4%',
        width: 380, height: 380,
        background: 'radial-gradient(circle, rgba(255,107,107,0.09) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, py: 10 }}>
        <Container maxWidth="md">

          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, display: 'block', mb: 2 }}>
              Got Questions?
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.2rem' }, fontWeight: 800, color: '#fff', mb: 2 }}>
              Frequently Asked{' '}
              <Box component="span" className="gradient-text">Questions</Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', lineHeight: 1.8 }}>
              Everything you need to know about our services, pricing, and process.
            </Typography>
          </Box>

          {/* Category tabs */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 5, justifyContent: 'center' }}>
            {faqCategories.map((cat, i) => (
              <Box
                key={i}
                onClick={() => { setActiveCategory(i); setExpanded(false); }}
                sx={{
                  px: 2.5, py: 1,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  background: activeCategory === i
                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)'
                    : 'rgba(255,107,107,0.08)',
                  color: activeCategory === i ? '#2d3436' : '#fff',
                  border: activeCategory === i ? 'none' : '1px solid rgba(255,107,107,0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': { background: activeCategory === i ? undefined : 'rgba(255,107,107,0.15)' },
                }}
              >
                {cat.category}
              </Box>
            ))}
          </Box>

          {/* Accordion FAQs */}
          <Box sx={{ mb: 8 }}>
            {faqCategories[activeCategory].faqs.map((faq, i) => (
              <Accordion
                key={i}
                expanded={expanded === `panel-${i}`}
                onChange={handleChange(`panel-${i}`)}
                sx={{
                  mb: 1.5,
                  background: 'rgba(255,107,107,0.04)',
                  border: expanded === `panel-${i}`
                    ? '1px solid rgba(255,107,107,0.3)'
                    : '1px solid rgba(255,107,107,0.1)',
                  borderRadius: '12px !important',
                  backdropFilter: 'blur(10px)',
                  '&:before': { display: 'none' },
                  transition: 'border-color 0.2s ease',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                  sx={{ px: 3, py: 0.5 }}
                >
                  <Typography sx={{ fontWeight: 600, color: '#fff', fontSize: '0.97rem' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Still have questions CTA */}
          <Box sx={{
            textAlign: 'center', p: { xs: 4, md: 5 },
            background: 'rgba(255,107,107,0.05)',
            border: '1px solid rgba(255,107,107,0.15)',
            borderRadius: '20px',
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
              Still Have Questions?
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              Our team is ready to help — reach out directly or book a free consultation.
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button
                  variant="contained"
                  onClick={() => navigate('/contact')}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ffd93d 100%)',
                    color: '#2d3436', fontWeight: 700, px: 4,
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(255,107,107,0.3)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Contact Us
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/book-service')}
                  sx={{
                    borderColor: 'rgba(255,107,107,0.4)', color: '#fff', px: 4,
                    '&:hover': { borderColor: 'primary.main', background: 'rgba(255,107,107,0.08)' },
                  }}
                >
                  Book a Service
                </Button>
              </Grid>
            </Grid>
          </Box>

        </Container>
      </Box>
    </Box>
  );
}

export default FAQ;
